/**
 * Mapeamento de notificações por "família" de evento (prefixo antes do ".").
 * O backend usa event_type namespaced, ex.: "transfer.accepted", "order.paid".
 * O front renderiza genericamente: agrupa ícone/rótulo pelo prefixo.
 */

const DEFAULT_ICON = "pi-bell";

/** Índices (value) das abas da página do evento do produtor. */
const PRODUCER_EVENT_GUESTS_TAB = "4";
const PRODUCER_EVENT_COLLABORATORS_TAB = "5";

/** Prefixo do event_type -> ícone PrimeNG (classe `pi`). */
const EVENT_TYPE_ICON_BY_PREFIX: Record<string, string> = {
  invitation: "pi-gift",
  collaborator_invitation: "pi-users",
  transfer: "pi-send",
  order: "pi-ticket",
  seller_payment: "pi-wallet",
  admin_task: "pi-clipboard",
  coupon: "pi-tag",
};

/** Prefixo do event_type -> rótulo curto em pt-BR (categoria). */
const EVENT_TYPE_LABEL_BY_PREFIX: Record<string, string> = {
  invitation: "Convite",
  collaborator_invitation: "Colaborador",
  transfer: "Transferência",
  order: "Pedido",
  seller_payment: "Pagamento",
  admin_task: "Solicitação",
  coupon: "Cupom",
};

/**
 * Prefixo do event_type -> rota real do frontend (telas de lista com abas).
 * Os action_url vindos do backend (ex.: /orders/:id, /my-transfers/sent)
 * não correspondem às rotas deste front, então normalizamos por família.
 */
const FRONTEND_ROUTE_BY_PREFIX: Record<string, string> = {
  order: "/my-orders",
  seller_payment: "/seller-orders",
  // coupon.* (created/redeemed/closed/deactivated) -> lista global de cupons do
  // produtor. Normalizamos para a lista (em vez do action_url /coupons/:id do
  // backend) porque a tela de detalhe exige o event_id em query param.
  coupon: "/coupons",
};

/**
 * Famílias que caem em /my-invitations -> aba (query param `tab`) correta.
 * Os valores batem com o enum DISPLAY_MOBILE_OPTIONS_ENUM da página.
 */
const MY_INVITATIONS_TAB_BY_PREFIX: Record<string, string> = {
  invitation: "courtesies",
  transfer: "transfers",
  collaborator_invitation: "employee",
};

function prefixOf(eventType: string | null | undefined): string {
  return (eventType ?? "").split(".")[0];
}

/**
 * Resolve a rota de destino do frontend para uma notificação.
 * Usa o mapeamento por família; se desconhecida, cai no action_url do backend
 * (pode ser nulo, indicando que não há navegação).
 */
export function resolveNotificationUrl(notification: {
  event_type: string;
  action_url: string | null;
  data?: Record<string, unknown> | null;
  notifiable_id?: number | null;
}): string | null {
  const prefix = prefixOf(notification.event_type);

  // admin_task.* aponta para a solicitação (notifiable = AdminTask).
  // Backend usa /admin-tasks/:id (dashboard); no front é /solicitations/:id.
  if (prefix === "admin_task" && notification.notifiable_id != null) {
    return `/solicitations/${notification.notifiable_id}`;
  }

  // Convite de colaborador aceito: notifica o ORGANIZADOR -> leva à página
  // do evento, já na aba de Colaboradores (tab 5).
  if (notification.event_type === "collaborator_invitation.accepted") {
    const eventId = notification.data?.["event_id"];
    if (eventId !== undefined && eventId !== null && eventId !== "") {
      return `/producer-area/event/${eventId}?tab=${PRODUCER_EVENT_COLLABORATORS_TAB}`;
    }
  }

  // Convite (cortesia) aceito/recusado: notifica o ORGANIZADOR -> leva à
  // página do evento, já na aba de Convidados (tab 4).
  if (
    notification.event_type === "invitation.accepted" ||
    notification.event_type === "invitation.declined"
  ) {
    const eventId = notification.data?.["event_id"];
    if (eventId !== undefined && eventId !== null && eventId !== "") {
      return `/producer-area/event/${eventId}?tab=${PRODUCER_EVENT_GUESTS_TAB}`;
    }
  }

  // Notificações recebidas pelo usuário (convite/cortesia, transferência,
  // colaborador) -> /my-invitations já na aba correta.
  const invitationsTab = MY_INVITATIONS_TAB_BY_PREFIX[prefix];
  if (invitationsTab) {
    return `/my-invitations?tab=${invitationsTab}`;
  }

  const base = FRONTEND_ROUTE_BY_PREFIX[prefix] ?? notification.action_url;

  if (!base) {
    return null;
  }

  // seller_payment.paid carrega o pedido confirmado em data.order_id:
  // direciona já filtrando a lista de vendas por esse pedido.
  if (prefix === "seller_payment") {
    const orderId = notification.data?.["order_id"];
    if (orderId !== undefined && orderId !== null && orderId !== "") {
      return `${base}?order_id=${encodeURIComponent(String(orderId))}`;
    }
  }

  return base;
}

export function iconForEventType(eventType: string | null | undefined): string {
  return EVENT_TYPE_ICON_BY_PREFIX[prefixOf(eventType)] ?? DEFAULT_ICON;
}

export function labelForEventType(
  eventType: string | null | undefined,
): string {
  return EVENT_TYPE_LABEL_BY_PREFIX[prefixOf(eventType)] ?? "Notificação";
}
